/** Dakkapel configurator catalogs + helpers (config-go). Dimensions stored in mm. */

export const DAKKAPEL_MIN_WIDTH_MM = 1000;
export const DAKKAPEL_MAX_WIDTH_MM = 7500;
export const DAKKAPEL_MIN_HEIGHT_MM = 1500;
export const DAKKAPEL_MAX_HEIGHT_MM = 7500;
export const DAKKAPEL_DEFAULT_WIDTH_MM = 3000;
export const DAKKAPEL_DEFAULT_HEIGHT_MM = 1500;
/** Clear glass / pane width on the sub-ruler (structure is counted separately). */
export const DAKKAPEL_MIN_ELEMENT_WIDTH_MM = 150;
export const DAKKAPEL_MAX_ELEMENT_WIDTH_MM = 1000;
/** Default clear-pane width for the first part and for each newly added part. */
export const DAKKAPEL_DEFAULT_PART_WIDTH_MM = 750;
export const DAKKAPEL_MAX_ELEMENTS = 14;

/** Outer left/right posts marked "x" — fixed, not part of the sub-ruler share. */
export const DAKKAPEL_OUTER_FRAME_MM = 150;
/**
 * Every vertical divider marked "o": the jamb next to each outer post, plus each
 * mullion between panes. Count is always (parts + 1).
 */
export const DAKKAPEL_DIVIDER_MM = 70;

export const DAKKAPEL_ELEMENT_TYPES = [
  { id: 'fixed-window', labelKey: 'dakkapel.elementFixedWindow' },
  { id: 'tilt-turn-left', labelKey: 'dakkapel.elementTiltTurnLeft' },
  { id: 'tilt-turn-right', labelKey: 'dakkapel.elementTiltTurnRight' },
  { id: 'tilt-only', labelKey: 'dakkapel.elementTiltOnly' },
  { id: 'fixed-casement', labelKey: 'dakkapel.elementFixedCasement' },
  { id: 'fixed-panel', labelKey: 'dakkapel.elementFixedPanel' },
];

/** Opening sash types (incl. legacy `tilt-turn` from older saves). */
export const DAKKAPEL_OPENING_ELEMENT_TYPE_IDS = new Set([
  'tilt-turn',
  'tilt-turn-left',
  'tilt-turn-right',
  'tilt-only',
]);

export const isDakkapelOpeningElementType = (type) =>
  DAKKAPEL_OPENING_ELEMENT_TYPE_IDS.has(type);

/** Map retired single Draai/kiep id to left hinge. */
export const normalizeDakkapelElementType = (type) =>
  type === 'tilt-turn' ? 'tilt-turn-left' : type;

export const DAKKAPEL_MATERIALS = [
  { id: 'trespa', labelKey: 'dakkapel.materialTrespa' },
  { id: 'keralit-vertical', labelKey: 'dakkapel.materialKeralitVertical' },
  { id: 'keralit-horizontal', labelKey: 'dakkapel.materialKeralitHorizontal' },
];

/**
 * Withdrawn from the picker but still readable, so quotes saved before the catalog was
 * trimmed keep showing a proper label instead of a raw id.
 */
const DAKKAPEL_RETIRED_MATERIALS = [
  { id: 'red-cedar', labelKey: 'dakkapel.materialRedCedar' },
  { id: 'red-cedar-horizontal', labelKey: 'dakkapel.materialRedCedarHorizontal' },
  { id: 'red-cedar-vertical', labelKey: 'dakkapel.materialRedCedarVertical' },
  { id: 'wbp-multiplex', labelKey: 'dakkapel.materialWbp' },
];

export const getDakkapelMaterialLabelKey = (id) =>
  [...DAKKAPEL_MATERIALS, ...DAKKAPEL_RETIRED_MATERIALS].find((m) => m.id === id)?.labelKey || null;

export const DAKKAPEL_SUN_PROTECTION = [
  { id: 'none', labelKey: 'dakkapel.sunNone' },
  { id: 'screens', labelKey: 'dakkapel.sunScreens' },
  { id: 'shutters', labelKey: 'dakkapel.sunShutters' },
];

export const DAKKAPEL_ROOF_STRUCTURES = [
  { id: 'purlins', labelKey: 'dakkapel.roofPurlins' },
  { id: 'rafters', labelKey: 'dakkapel.roofRafters' },
];

export const DAKKAPEL_ROOF_CONNECTIONS = [
  { id: 'ubiflex-black', labelKey: 'dakkapel.connectionUbiflexBlack' },
  { id: 'ubiflex-red', labelKey: 'dakkapel.connectionUbiflexRed' },
  { id: 'lead-18', labelKey: 'dakkapel.connectionLead18' },
];

export const mmToCm = (mm) => Math.round(Number(mm) / 10);
export const cmToMm = (cm) => Math.round(Number(cm) * 10);

/** Overall width/height step on dakkapel.nl (5 cm). */
export const DAKKAPEL_DIMENSION_STEP_MM = 50;
/** Per-element width step on dakkapel.nl (5 cm). */
export const DAKKAPEL_ELEMENT_WIDTH_STEP_MM = 50;

export const stepDakkapelDimensionMm = (currentMm, direction, minMm, maxMm) => {
  const step = DAKKAPEL_DIMENSION_STEP_MM;
  const next =
    direction > 0
      ? Math.min(maxMm, Math.round(Number(currentMm) || 0) + step)
      : Math.max(minMm, Math.round(Number(currentMm) || 0) - step);
  return next;
};

/** Same 5 cm steps as the reference configurator, with a 1 cm nudge near the cap. */
export const stepDakkapelElementWidthMm = (currentMm, direction, maxMm = DAKKAPEL_MAX_ELEMENT_WIDTH_MM) => {
  const minMm = DAKKAPEL_MIN_ELEMENT_WIDTH_MM;
  const step = DAKKAPEL_ELEMENT_WIDTH_STEP_MM;
  let next = Math.round(Number(currentMm) || 0);
  if (direction > 0) {
    next =
      next + step <= maxMm - step ? next + step : Math.min(maxMm, next + 10);
  } else {
    next =
      next - step >= minMm + step ? next - step : Math.max(minMm, next - 10);
  }
  return Math.min(maxMm, Math.max(minMm, next));
};

export const clampDakkapelWidthMm = (mm) =>
  Math.min(DAKKAPEL_MAX_WIDTH_MM, Math.max(DAKKAPEL_MIN_WIDTH_MM, Math.round(Number(mm) || DAKKAPEL_DEFAULT_WIDTH_MM)));

export const clampDakkapelHeightMm = (mm) =>
  Math.min(DAKKAPEL_MAX_HEIGHT_MM, Math.max(DAKKAPEL_MIN_HEIGHT_MM, Math.round(Number(mm) || DAKKAPEL_DEFAULT_HEIGHT_MM)));

/** Jambs (2) + mullions (parts - 1) — every thin "o" before majors are substituted. */
export const getDakkapelDividerCount = (partCount) => Math.max(1, Math.round(Number(partCount) || 1)) + 1;

/** Major structural dividers removed — use narrow fixed-panel parts (min 150 mm) instead. */
export const getDakkapelMajorDividerCount = () => 0;

/** Single run of parts; no major-divider grouping. */
export const getDakkapelPaneGroupSizes = (partCount) => {
  const n = Math.max(2, Math.round(Number(partCount) || 2));
  return [n];
};

/** 0-based pane indexes after which a major divider was drawn — always none. */
export const getDakkapelMajorDividerAfterIndexes = () => [];

/**
 * Fixed structure: outer posts (150 mm) + thin mullions (70 mm) between every part.
 */
export const getDakkapelStructureWidthMm = (partCount) => {
  const n = Math.max(2, Math.round(Number(partCount) || 2));
  return 2 * DAKKAPEL_OUTER_FRAME_MM + getDakkapelDividerCount(n) * DAKKAPEL_DIVIDER_MM;
};

/** Width the sub-ruler panes are allowed to share. */
export const getDakkapelGlassBudgetMm = (overallWidthMm, partCount) => {
  const overall = clampDakkapelWidthMm(overallWidthMm);
  const n = Math.max(2, Math.round(Number(partCount) || 2));
  return Math.max(0, overall - getDakkapelStructureWidthMm(n));
};

/** Max parts that physically fit at min pane width (up to {@link DAKKAPEL_MAX_ELEMENTS}). */
export const getMaxDakkapelElements = (widthMm) => {
  const width = clampDakkapelWidthMm(widthMm);
  for (let n = DAKKAPEL_MAX_ELEMENTS; n >= 2; n -= 1) {
    const structure = getDakkapelStructureWidthMm(n);
    const budget = width - structure;
    if (budget >= n * DAKKAPEL_MIN_ELEMENT_WIDTH_MM) return n;
  }
  return 2;
};

/**
 * Auto part count from overall width (used when the user hasn't locked a manual count
 * in Stap 2). Width bands:
 *   ≤3000 → 2 | ≤4400 → 4 max | ≤5500 → 5 | ≤7000 → 6 | else → 7+
 */
export const getSuggestedDakkapelElementCount = (widthMm) => {
  const w = clampDakkapelWidthMm(widthMm);
  if (w <= 3000) return 2;
  if (w <= 3600) return 3;
  if (w <= 4400) return 4;
  if (w <= 4950) return 4;
  if (w <= 5500) return 5;
  if (w <= 6250) return 5;
  if (w <= 7000) return 6;
  return 7;
};

/** Suggested count unless Stap 2 locked a manual choice; always clamped to what fits. */
export const resolveDakkapelElementCount = (widthMm, currentCount, elementCountManual = false) => {
  const max = getMaxDakkapelElements(widthMm);
  const suggested = getSuggestedDakkapelElementCount(widthMm);
  const chosen = elementCountManual
    ? Math.round(Number(currentCount) || suggested)
    : suggested;
  return Math.min(max, Math.max(2, chosen));
};

/** 750 mm anchors on the first and last part of the elevation. */
const getGroupAnchorIndices = (count, _overallWidthMm, extraIndices = []) => {
  const n = Math.max(1, Math.round(Number(count) || 1));
  const anchors = new Set([0]);
  if (n >= 3) anchors.add(n - 1);
  extraIndices.forEach((index) => {
    if (index >= 0 && index < n) anchors.add(index);
  });
  return anchors;
};

const isCappedIndex = (index, count, overallWidthMm) =>
  getGroupAnchorIndices(count, overallWidthMm).has(index);

/** The cap has to shrink if the flexible panes would otherwise be starved. */
const outerWidthCap = (count, glassBudgetMm, overallWidthMm) => {
  const anchors = getGroupAnchorIndices(count, overallWidthMm);
  const outer = Math.max(1, anchors.size);
  const flexible = count - outer;
  return Math.min(
    DAKKAPEL_MAX_ELEMENT_WIDTH_MM,
    Math.max(
      DAKKAPEL_MIN_ELEMENT_WIDTH_MM,
      Math.floor((glassBudgetMm - DAKKAPEL_MIN_ELEMENT_WIDTH_MM * flexible) / outer)
    )
  );
};

/**
 * The one rule every width path funnels through. Widths are clear pane sizes that must
 * sum to the glass budget (overall minus fixed structure). `pinIndex` holds a part the
 * user just typed into at exactly that width while its flexible peers absorb the difference.
 */
const applyElementWidthRule = (widths, glassBudgetMm, overallWidthMm, pinIndex = null) => {
  const total = Math.round(glassBudgetMm);
  const n = widths.length;
  if (n <= 1) return [total];

  const cap = outerWidthCap(n, total, overallWidthMm);
  const asked = (i) =>
    Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, Math.round(Number(widths[i]) || 0));

  const out = new Array(n).fill(0);
  const flexible = [];
  for (let i = 0; i < n; i += 1) {
    if (isCappedIndex(i, n, overallWidthMm)) out[i] = Math.min(cap, asked(i));
    else flexible.push(i);
  }

  let slack = total - out.reduce((sum, w) => sum + w, 0);
  let sharing = flexible;

  if (pinIndex != null && flexible.includes(pinIndex) && flexible.length > 1) {
    const peers = flexible.filter((i) => i !== pinIndex);
    const room = slack - DAKKAPEL_MIN_ELEMENT_WIDTH_MM * peers.length;
    out[pinIndex] = Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, Math.min(asked(pinIndex), room));
    slack -= out[pinIndex];
    sharing = peers;
  }

  // Flexible panes keep their relative sizes, and the last one takes the rounding drift.
  const weight = sharing.reduce((sum, i) => sum + asked(i), 0);
  let used = 0;
  sharing.forEach((i, k) => {
    if (k === sharing.length - 1) {
      out[i] = Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, slack - used);
      return;
    }
    const share =
      weight > 0 ? Math.round((asked(i) / weight) * slack) : Math.floor(slack / sharing.length);
    out[i] = Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, share);
    used += out[i];
  });

  return out;
};

/** First/last of each pane group default to 750mm; each newly added part starts at 750mm. */
const defaultElementWidths = (count, glassBudgetMm, overallWidthMm, { newPartIndices = [] } = {}) => {
  const n = Math.max(1, count);
  if (n === 1) return [Math.round(glassBudgetMm)];

  const overall = clampDakkapelWidthMm(overallWidthMm);
  const anchorIndices = getGroupAnchorIndices(n, overall, newPartIndices);
  const cap = outerWidthCap(n, glassBudgetMm, overall);
  const anchorWidth = Math.min(DAKKAPEL_DEFAULT_PART_WIDTH_MM, cap);

  const flexShare = Math.max(
    DAKKAPEL_MIN_ELEMENT_WIDTH_MM,
    Math.floor(
      (glassBudgetMm - anchorWidth * anchorIndices.size) / Math.max(1, n - anchorIndices.size)
    )
  );
  const asked = Array.from({ length: n }, (_, i) =>
    anchorIndices.has(i) ? anchorWidth : flexShare
  );
  return applyElementWidthRule(asked, glassBudgetMm, overall);
};

/** Which parts the UI should cap: the 750mm anchors at each group end. */
export const getDakkapelElementMaxWidthMm = (index, count, overallWidthMm) =>
  isCappedIndex(index, count, overallWidthMm) ? DAKKAPEL_MAX_ELEMENT_WIDTH_MM : null;

export const createDakkapelElements = (count, widthMm, previous = []) => {
  const max = getMaxDakkapelElements(widthMm);
  const n = Math.min(max, Math.max(2, Math.round(Number(count) || 2)));
  const overall = clampDakkapelWidthMm(widthMm);
  const glassBudget = getDakkapelGlassBudgetMm(overall, n);
  const prev = Array.isArray(previous) ? previous : [];
  let widths;

  if (prev.length === n) {
    widths = normalizeElementWidths(
      prev.map((el) => el.widthMm),
      overall,
      n
    );
  } else {
    const newPartIndices =
      prev.length > 0 && n > prev.length
        ? Array.from({ length: n - prev.length }, (_, k) => prev.length + k)
        : [];
    widths = defaultElementWidths(n, glassBudget, overall, { newPartIndices });
  }

  return widths.map((w, i) => {
    let type = previous[i]?.type;
    if (!type) {
      // Default: all fixed windows; middle opens as draai/kiep links when 3+
      type = n >= 3 && i === Math.floor(n / 2) ? 'tilt-turn-left' : 'fixed-window';
    }
    return {
      id: previous[i]?.id || `el-${i + 1}`,
      type: normalizeDakkapelElementType(type),
      widthMm: w,
    };
  });
};

/** Keep the current part count when width changes; only shrink if it no longer fits. */
export const applyWidthDrivenLayout = (widthMm, previousElements = []) => {
  const width = clampDakkapelWidthMm(widthMm);
  const max = getMaxDakkapelElements(width);
  const prevLen = Array.isArray(previousElements) ? previousElements.length : 0;
  const count =
    prevLen >= 2
      ? Math.min(max, prevLen)
      : Math.min(max, Math.max(2, getSuggestedDakkapelElementCount(width)));
  return {
    width,
    elements: createDakkapelElements(count, width, previousElements),
  };
};

export const normalizeElementWidths = (widths, overallWidthMm, partCount = null) => {
  const overall = clampDakkapelWidthMm(overallWidthMm);
  const list = (widths || []).map((w) => Math.round(Number(w) || 0));
  const n = partCount != null ? Math.max(1, partCount) : Math.max(1, list.length);
  const glassBudget = getDakkapelGlassBudgetMm(overall, n);
  if (list.length === 0) return defaultElementWidths(n, glassBudget, overall);
  if (n === 1) return [glassBudget];

  // A hand-tuned split that already obeys the glass-budget rule is kept as-is;
  // anything else (including old configs that still summed to the overall width) is rebuilt.
  const sized = list.length === n ? list : defaultElementWidths(n, glassBudget, overall);
  if (sized !== list) return sized;

  const cap = outerWidthCap(n, glassBudget, overall);
  const obeysRule =
    sized.reduce((a, b) => a + b, 0) === glassBudget &&
    sized.every((w) => w >= DAKKAPEL_MIN_ELEMENT_WIDTH_MM) &&
    sized.every((w, i) => !isCappedIndex(i, n, overall) || w <= cap);
  return obeysRule ? sized : defaultElementWidths(n, glassBudget, overall);
};

export const updateElementWidth = (elements, index, nextWidthMm, totalWidthMm) => {
  const overall = clampDakkapelWidthMm(totalWidthMm);
  const next = elements.map((el) => ({ ...el }));
  if (!next[index]) return next;
  const n = next.length;
  const glassBudget = getDakkapelGlassBudgetMm(overall, n);
  if (n === 1) return [{ ...next[0], widthMm: glassBudget }];

  const requested = Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, Math.round(Number(nextWidthMm) || 0));
  const widths = next.map((el, i) => (i === index ? requested : el.widthMm));

  const flexible = widths.map((_, i) => i).filter((i) => !isCappedIndex(i, n, overall));
  const peers = flexible.filter((i) => i !== index);

  // A flexible pane with no flexible peers is purely the remainder, so honouring an edit
  // there means letting the capped outer panes give way instead.
  if (flexible.includes(index) && peers.length === 0) {
    const outer = widths.map((_, i) => i).filter((i) => isCappedIndex(i, n, overall));
    const cap = outerWidthCap(n, glassBudget, overall);
    const room = Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM * outer.length, glassBudget - requested);
    const each = Math.floor(room / outer.length);
    outer.forEach((i, k) => {
      const target = k === outer.length - 1 ? room - each * (outer.length - 1) : each;
      widths[i] = Math.min(cap, Math.max(DAKKAPEL_MIN_ELEMENT_WIDTH_MM, target));
    });
  }

  const resolved = applyElementWidthRule(widths, glassBudget, overall, index);
  return next.map((el, i) => ({ ...el, widthMm: resolved[i] }));
};

/** Switching to a fixed panel snaps that part to the minimum width (divider use). */
export const updateElementType = (elements, index, type, totalWidthMm) => {
  const normalized = normalizeDakkapelElementType(type);
  const next = elements.map((el, i) => (i === index ? { ...el, type: normalized } : el));
  if (normalized === 'fixed-panel' && next[index]) {
    return updateElementWidth(next, index, DAKKAPEL_MIN_ELEMENT_WIDTH_MM, totalWidthMm);
  }
  return next;
};

export const buildDefaultDakkapelConfig = () => {
  const width = DAKKAPEL_DEFAULT_WIDTH_MM;
  const height = DAKKAPEL_DEFAULT_HEIGHT_MM;
  return {
    productType: 'dakkapel',
    width,
    height,
    quantity: 1,
    elements: createDakkapelElements(getSuggestedDakkapelElementCount(width), width),
    elementCountManual: false,
    material: 'trespa',
    glazingType: 'double',
    // Recessed is the only frame type built now, so it is locked rather than offered.
    frameType: 'recessed',
    claddingColor: 'ral-9001',
    frameColor: 'ral-9001',
    fasciaColor: 'ral-9001',
    tiltTurnColor: 'ral-9001',
    sunProtection: 'none',
    insectScreens: 0,
    permitApplication: false,
    debrisRemoval: false,
    roofStructure: 'purlins',
    roofConnection: 'ubiflex-black',
    airco: false,
    ventilationGrilles: 0,
  };
};

export const normalizeDakkapelConfig = (loaded = {}) => {
  const base = buildDefaultDakkapelConfig();
  const width = clampDakkapelWidthMm(loaded.width ?? base.width);
  const height = clampDakkapelHeightMm(loaded.height ?? base.height);
  const elementCountManual = Boolean(loaded.elementCountManual);
  const count = resolveDakkapelElementCount(
    width,
    Array.isArray(loaded.elements) ? loaded.elements.length : base.elements.length,
    elementCountManual
  );
  const elements =
    Array.isArray(loaded.elements) && loaded.elements.length > 0
      ? createDakkapelElements(count, width, loaded.elements)
      : createDakkapelElements(count, width, base.elements);

  return {
    ...base,
    ...loaded,
    productType: 'dakkapel',
    width,
    height,
    elements,
    elementCountManual,
    quantity: Math.max(1, Math.round(Number(loaded.quantity) || 1)),
    insectScreens: Math.max(0, Math.round(Number(loaded.insectScreens) || 0)),
    ventilationGrilles: Math.max(0, Math.round(Number(loaded.ventilationGrilles) || 0)),
    permitApplication: Boolean(loaded.permitApplication),
    debrisRemoval: Boolean(loaded.debrisRemoval),
    airco: Boolean(loaded.airco),
    glazingType: loaded.glazingType === 'triple' ? 'triple' : 'double',
  };
};
